import { Button, Tooltip } from '@ibsys/kit';
import { BookmarkXIcon, FilterXIcon, SaveIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectionSavePopover, TablePresetsPopover } from '@/features/TablePresets';
import type { FileDownloadFormat } from '@/shared/lib';
import { DownloadFormatButton } from '@/shared/ui';
import { PRESETS_WIDTH_RESERVE_CLASS } from './ContentSearch/controlRowClasses';
import type { AppliedSelection } from './model/useExternalSelection';

/** Пропы панели действий над списком диалогов */
export type DialogListToolbarProps = {
  /** Есть донастройка поверх слоя (или обычные фильтры) — показывать кнопку сброса */
  canReset: boolean;
  /** Сброс донастройки к базе слоя (пресету/выборке) или к полному списку, если слоя нет */
  onResetFilters: () => void;
  /** Активный пресет донастроен — показывать кнопку записи донастройки в него */
  canSaveActivePreset: boolean;
  /** Имя активного пресета — подставляется в подсказку кнопки сохранения изменений */
  activePresetName?: string;
  /** Записать текущую донастройку в активный пресет */
  onSaveActivePreset: () => void;
  /** Активна несохранённая выборка перехода — показывать кнопку её снятия */
  canDeselectSelection: boolean;
  /** Снятие несохранённой выборки: возврат к отложенному личному виду */
  onDeselectSelection: () => void;
  /** Имя таблицы — ключ в settings.tables, используется для управления пресетами настроек */
  tableName: string;
  /** Внешняя выборка, которую предлагается сохранить в пресет; `null` — предлагать нечего */
  unsavedSelection: AppliedSelection | null;
  /** Пользователь скрыл предложение сохранить выборку */
  onSelectionHintDismiss: () => void;
  /** Выгрузка текущего списка файлом в выбранном формате */
  onDownloadList: (format: FileDownloadFormat) => void;
  /** Файл списка формируется — повторное скачивание не запускается */
  isDownloadPending: boolean;
  /** Пользователь выбрал в списке пресетов другой пресет или снял активный */
  onActivePresetChange: (presetId: string | null) => void;
};

/**
 * Панель начинается с плашки — кнопки активного пресета с рамкой, — когда ни одна кнопка-иконка
 * левее неё не показана. Признак нужен строке управления для выравнивания левого края блоков и
 * живёт рядом с кнопками, чтобы их добавление или удаление не ломало выравнивание молча.
 */
export const isToolbarStartPlated = ({
  canReset,
  canSaveActivePreset,
  canDeselectSelection,
  activePresetName,
}: Pick<
  DialogListToolbarProps,
  'canReset' | 'canSaveActivePreset' | 'canDeselectSelection' | 'activePresetName'
>): boolean => Boolean(activePresetName) && !canReset && !canSaveActivePreset && !canDeselectSelection;

/**
 * Панель действий над списком диалогов в строке поиска: сброс донастройки, запись её в активный
 * пресет, снятие несохранённой выборки перехода, управление пресетами и выгрузка списка файлом.
 * Кнопки-иконки показываются по соответствующему признаку доступности.
 */
export const DialogListToolbar: FC<DialogListToolbarProps> = ({
  canReset,
  onResetFilters,
  canSaveActivePreset,
  activePresetName,
  onSaveActivePreset,
  canDeselectSelection,
  onDeselectSelection,
  tableName,
  unsavedSelection,
  onSelectionHintDismiss,
  onDownloadList,
  isDownloadPending,
  onActivePresetChange,
}) => {
  const { t } = useTranslation(['dialogs', 'tablePresets']);

  return (
    <div className="flex items-center gap-2 min-w-0">
      {canReset && (
        // Только иконка: название выводится в подсказке при наведении.
        <Tooltip tooltip={t('dialogs:dialogListSection.resetFilters.label')}>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={t('dialogs:dialogListSection.resetFilters.label')}
            icon={<FilterXIcon className="size-4" />}
            onClick={onResetFilters}
          />
        </Tooltip>
      )}
      {canSaveActivePreset && (
        // Парная кнопке отмены изменений: записывает донастройку в активный пресет из строки
        // поиска, без открытия выпадающего списка.
        <Tooltip tooltip={t('tablePresets:saveToActive', { name: activePresetName })}>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={t('tablePresets:saveToActive', { name: activePresetName })}
            icon={<SaveIcon className="size-4" />}
            onClick={onSaveActivePreset}
          />
        </Tooltip>
      )}
      {canDeselectSelection && (
        // Снятие несохранённой выборки перехода; у сохранённого пресета снятие живёт в его строке.
        <Tooltip tooltip={t('dialogs:dialogListSection.deselectSelection.label')}>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={t('dialogs:dialogListSection.deselectSelection.label')}
            icon={<BookmarkXIcon className="size-4" />}
            onClick={onDeselectSelection}
          />
        </Tooltip>
      )}
      <SelectionSavePopover tableName={tableName} selection={unsavedSelection} onDismiss={onSelectionHintDismiss}>
        <TablePresetsPopover
          tableName={tableName}
          widthReserveClassName={PRESETS_WIDTH_RESERVE_CLASS}
          onActivePresetChange={onActivePresetChange}
        />
      </SelectionSavePopover>
      <DownloadFormatButton onDownload={onDownloadList} isPending={isDownloadPending} className="shrink-0" />
    </div>
  );
};
